'use client';
/* eslint-disable @nrwl/nx/enforce-module-boundaries */
import { Switch, Checkbox } from '@quadminds/mindkit';
import { BadgeCustom } from '@quadminds/mindy-app/components/common/CustomBadge/CustomBadge';
import { useByPassContext } from '@quadminds/mindy-app/providers/ByPass/ByPass.provider';
import type { FileItem } from '@quadminds/mindy-app/types/File/File.types';
import { getFileIcon } from '@quadminds/mindy-app/utils/getFileIcon';
import { formatFileSize } from '@quadminds/mindy-app/utils/utils';
import { MindyKnowledgePermissions } from '@quadminds/mindy-shared';
import { IAuthMeV2OutputDto } from '@quadminds/shared/auth-system';
import { useQueryClient } from '@tanstack/react-query';
import { FolderClosed, ArchiveX } from 'lucide-react';
import type React from 'react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { FileActions } from '../FileActions/FileActions';
import TableWidgets from '../Table/TableWidgets.component';

interface FileGridProps {
    readonly files: FileItem[];
    readonly onNavigate: (path: string) => void;
    readonly onMoveFile: (fileId: string, targetPath: string) => void;
    readonly onRename: (item: FileItem) => void;
    readonly onDelete: (item: FileItem) => void;
    readonly onInternalDragStart: (fileId: string) => void;
    readonly onInternalDragEnd: () => void;
    readonly draggedFileId: string | null;
    readonly onMoveToFolder: (file: FileItem) => void;
    readonly onToggleActive: (file: FileItem, active: boolean) => void;
    readonly selectedItems: string[];
    readonly onSelectItem: (itemId: string, selected: boolean) => void;
    readonly onPreviewFile: (file: FileItem) => void;
    readonly onDownload: (file: FileItem) => void;
    readonly onReplace: (file: FileItem) => void;
}

export function FileGrid({
    files,
    onNavigate,
    onMoveFile,
    onRename,
    onDelete,
    onInternalDragStart,
    onInternalDragEnd,
    draggedFileId,
    onMoveToFolder,
    onToggleActive,
    selectedItems,
    onSelectItem,
    onPreviewFile,
    onDownload,
    onReplace,
}: FileGridProps) {
    const [dropTargets, setDropTargets] = useState<Record<string, boolean>>({});
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const userInfo: IAuthMeV2OutputDto | undefined = queryClient.getQueryData(['userInfo']); // Retrieve userInfo from the cache
    const { isImpersonated } = useByPassContext();
    const currentUserPermissions = userInfo?.permissions || [];

    useEffect(() => {
        if (!draggedFileId) {
            setDropTargets({});
        }
    }, [draggedFileId]);

    const handleDragStart = (e: React.DragEvent, file: FileItem) => {
        if (file.type === 'file') {
            e.dataTransfer.effectAllowed = 'move';
            e.dataTransfer.setData(
                'application/json',
                JSON.stringify({
                    fileId: file.id,
                    fileName: file.name,
                    fileType: file.type,
                }),
            );
            onInternalDragStart(file.id);
        }
    };

    const handleDragEnd = () => {
        onInternalDragEnd();
    };

    const handleDragOver = (e: React.DragEvent, folder: FileItem) => {
        e.preventDefault();
        e.stopPropagation();

        // Solo permitir soltar en carpetas y no en la carpeta que contiene el archivo arrastrado
        if (folder.type === 'folder' && folder.id !== draggedFileId) {
            e.dataTransfer.dropEffect = 'move';
            setDropTargets((prev) => ({ ...prev, [folder.id]: true }));
        }
    };

    const handleDragLeave = (e: React.DragEvent, folderId: string) => {
        e.preventDefault();
        e.stopPropagation();
        setDropTargets((prev) => ({ ...prev, [folderId]: false }));
    };

    const handleDrop = (e: React.DragEvent, folder: FileItem) => {
        e.preventDefault();
        e.stopPropagation();

        try {
            // Intentar obtener los datos del archivo arrastrado
            const jsonData = e.dataTransfer.getData('application/json');
            if (jsonData) {
                const data = JSON.parse(jsonData);

                if (data.fileId && folder.type === 'folder') {
                    onMoveFile(data.fileId, folder.id);
                }
            }
        } catch (error) {
            console.error('Error parsing drag data:', error);
        }

        setDropTargets({});
    };

    // Función para manejar el cambio del switch directamente
    const handleSwitchChange = (e: React.MouseEvent, file: FileItem) => {
        e.stopPropagation();
        onToggleActive(file, !file.active);
    };

    const handleFileClick = (e: React.MouseEvent, file: FileItem) => {
        if (file.type === 'folder') {
            onNavigate(file.id);
        } else if (
            !(e.target as HTMLElement).closest('.checkbox-container') &&
            !(e.target as HTMLElement).closest('.actions-container')
        ) {
            onPreviewFile(file);
        }
    };

    // Función para renderizar una carpeta
    const renderFolder = (file: FileItem) => {
        return (
            <div className="flex flex-col h-full items-center justify-center gap-2 p-3">
                <FolderClosed className="h-12 w-12 text-primary" />
                <div className="text-sm font-medium text-center truncate w-full">{file.name}</div>
                {file.size && <div className="text-xs text-muted-foreground mt-1">{formatFileSize(file.size)}</div>}
            </div>
        );
    };

    // Función para renderizar un archivo
    const renderFile = (file: FileItem) => {
        const FileTypeIcon = getFileIcon(file.name);
        const hasToDisableSwitch =
            !isImpersonated && !currentUserPermissions.includes(MindyKnowledgePermissions.UpdateKnowledge);
        return (
            <div className="flex flex-col h-full w-full gap-2">
                <div className="flex flex-col flex-grow items-center justify-center gap-2 p-3">
                    <FileTypeIcon className="h-12 w-12 text-secondary" />
                    <div className="text-sm font-medium text-center truncate w-full">{file.name}</div>
                    {file.size && (
                        <div className="text-xs text-muted-foreground -mt-2">{formatFileSize(file.size)}</div>
                    )}
                </div>
                <div className="flex items-center justify-between gap-2 w-full bg-gray-qm-50/30 dark:bg-foreground/10 py-1 px-2 rounded-b-[9px]">
                    <BadgeCustom variant={file.status === 'analyzed' ? 'success' : 'default'}>
                        {t(`knowledgeManager.filters.${file.status}`)}
                    </BadgeCustom>
                    <button className="flex items-center mt-1">
                        <button onClick={(e) => handleSwitchChange(e, file)}>
                            <Switch checked={file.active || false} className="scale-75" disabled={hasToDisableSwitch} />
                        </button>
                    </button>
                </div>
            </div>
        );
    };

    const showEditBtn = currentUserPermissions.includes(MindyKnowledgePermissions.UpdateKnowledge) || isImpersonated;
    return (
        <div className="w-full" data-testid="knowledge-section-grid">
            <div className="flex w-full justify-end items-center p-2 pb-0">
                <TableWidgets />
            </div>
            {files.length === 0 ? (
                <div className="flex w-full h-60 items-center justify-center p-4">
                    <div className="flex flex-col items-center space-y-2 w-full">
                        <ArchiveX className="text-secondary" />
                        <span className="text-center text-muted-foreground text-sm">No hay archivos o carpetas</span>
                    </div>
                </div>
            ) : (
                <div className="grid p-4 grid-cols-2 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                    {files
                        .map((file) => (
                            <button
                                key={file.id}
                                className={`group relative border rounded-lg transition-colors h-[200px] flex flex-col ${
                                    file.type === 'folder' && dropTargets[file.id]
                                        ? 'bg-primary/10 dark:bg-primary/20 border-primary'
                                        : 'hover:bg-primary/10 dark:hover:bg-primary/20'
                                } ${
                                    selectedItems.includes(file.id)
                                        ? 'bg-primary/5 dark:bg-primary/10 border-primary'
                                        : ''
                                }`}
                                draggable={file.type === 'file'}
                                onDragStart={(e) => handleDragStart(e, file)}
                                onDragEnd={handleDragEnd}
                                onDragOver={file.type === 'folder' ? (e) => handleDragOver(e, file) : undefined}
                                onDragLeave={file.type === 'folder' ? (e) => handleDragLeave(e, file.id) : undefined}
                                onDrop={file.type === 'folder' ? (e) => handleDrop(e, file) : undefined}
                                onClick={(e) => handleFileClick(e, file)}
                            >
                                <div className="absolute top-2 left-2 z-10 checkbox-container">
                                    <Checkbox
                                        checked={selectedItems.includes(file.id)}
                                        onCheckedChange={(checked) => onSelectItem(file.id, !!checked)}
                                        onClick={(e) => e.stopPropagation()}
                                        aria-label={`Seleccionar ${file.name}`}
                                    />
                                </div>

                                <div className="flex flex-col items-center flex-1">
                                    {file.type === 'folder' ? renderFolder(file) : renderFile(file)}
                                </div>

                                <button
                                    className="absolute top-2 right-2 opacity-0 group-hover:opacity-100 transition-opacity actions-container"
                                    onClick={(e) => e.stopPropagation()}
                                >
                                    {showEditBtn && (
                                        <FileActions
                                            file={file}
                                            onRename={onRename}
                                            onDelete={onDelete}
                                            onMoveToFolder={onMoveToFolder}
                                            onDownload={onDownload}
                                            onReplace={onReplace}
                                            iconSize="sm"
                                        />
                                    )}
                                </button>
                            </button>
                        ))
                        .reverse()}
                </div>
            )}
        </div>
    );
}

export default FileGrid;
